/* eslint-disable react/no-multi-comp */
/* eslint-disable react/jsx-props-no-spreading */
import React, { ReactNode } from "react"
import { ControllerRenderProps, RegisterOptions, UseControllerProps, useController } from "react-hook-form"

// participant step searchify selects
import {
    BillSelect,
    BillTermSelect,
    ContactSelect,
    PacContactSelect,
    CountySelect,
    CustomTagSelect,
    DistrictSelect,
    // @ts-expect-error
    ElectedOfficeSelect,
    EmailTemplateSelect as EmailTemplatePisSelect,
    GrassrootsActionCenterSettingsSelect,
    GrassrootsRegistrationPageSelect,
    IssueManagementSelect,
    LedgerSettingsSelect as LedgerSettingsPieSelect,
    OfficialSelect as OfficialPieSelect,
    PoliticalCommitteeSelect,
    PressContactSelect,
    PublicOrganizationSelect,
    RegulationSelect,
    StafferSelect,
    StateSelect,
    TeamSelect as TeamPieSelect,
    TrackingDashboardSelect as TrackingDashboardPieSelect,
    UserSelect,
} from "app/static/frontend/selects/containers/PieSelect"

import { Label } from "@/components"
import { useResponsiveSize } from "@/hooks"

interface PieSelectProps<T> extends Partial<UseControllerProps<T>> {
    // eslint-disable-next-line react/no-unused-prop-types
    SelectComponent?: React.FunctionComponent<
        {
            errorMessage?: string
            id: string
            isDisabled: boolean
            label: string
            model: object
            multi: boolean
            permanentFilter: object
            searchify: boolean
            searchifyCallBack: ({ searchifyFilters }: { searchifyFilters: object }) => void
            shouldOpenSearchifyModal: boolean
            shouldRenderNewSelect: boolean
            shouldBeUniqueId: boolean
            allowExpandUp?: boolean
            placeholder?: string
            singleSelectSearchify?: boolean
            clearable?: boolean
        } & ControllerRenderProps
    >
    label: string
    multi?: boolean
    permanentFilter?: object
    placeholder?: string
    singleSelectSearchify?: boolean
    tooltip?: string | ReactNode
    withAsterisk?: boolean
    disabled?: boolean
    searchify?: boolean
    clearable?: boolean
    className?: string
    onChange?: (value: object[]) => void
}

export const GenericSelect: any = <TFormValues,>({
    SelectComponent,
    name,
    label,
    // we don't want to set a default for this because it could override
    // label default props used in app/static/frontend/selects/containers/PieSelect.jsx
    labelPieSelect,
    extraFields,
    filterKey,
    onChange,
    onClear,
    tooltip,
    model,
    permanentFilter,
    rules,
    withAsterisk,
    placeholder,
    disabled = false,
    multi = true,
    singleSelectSearchify = false,
    shouldUnregister = true,
    searchify = true,
    clearable = true,
    className,
}: PieSelectProps<TFormValues> & {
    labelPieSelect?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    model: { [key: string]: any }
    rules?: RegisterOptions
} & { extraFields: any; filterKey: any; onChange: any; onClear: any; SelectComponent: any }) => {
    const {
        field,
        fieldState: { error },
    } = useController<TFormValues>({ name, rules, shouldUnregister })

    const { isMobile } = useResponsiveSize()

    return (
        // we disable the following line because we don't have access to the ID of the
        // underlying input element here, so we can't set htmlFor
        // eslint-disable-next-line jsx-a11y/label-has-associated-control
        <div className={className}>
            <Label label={label} tooltip={tooltip} withAsterisk={withAsterisk} className={className} />
            <SelectComponent
                allowExpandUp
                clearable={clearable}
                errorMessage={error?.message}
                id={name}
                isDisabled={disabled}
                label={labelPieSelect}
                extraFields={extraFields}
                filterKey={filterKey}
                model={model}
                multi={multi}
                permanentFilter={permanentFilter}
                // Don't render searchify on mobile
                searchify={searchify && !isMobile}
                shouldBeUniqueId
                shouldOpenSearchifyModal
                shouldRenderNewSelect
                searchifyCallBack={({ searchifyFilters }) =>
                    model.objects
                        .filter({
                            current_searchify_selection: JSON.stringify(searchifyFilters),
                            limit: 1500,
                            ...permanentFilter,
                        })
                        .GET()
                        .then((response) => {
                            if (onChange) {
                                onChange(response.objects[0])
                            }

                            if (multi) {
                                field.onChange(response.objects.map((object) => object.id))
                            } else {
                                field.onChange(response.objects[0]?.id)
                            }
                        })
                }
                singleSelectSearchify={singleSelectSearchify}
                {...field}
                // put the redefined onChange
                onChange={(value) => {
                    if (onChange) {
                        onChange(value)
                    }

                    if (multi) {
                        field.onChange(value.map((value) => value.id))
                    } else {
                        field.onChange(value.id)
                    }
                }}
                onClear={onClear}
                // using a spreader so we don't overwrite the default placeholder defined
                // in /app/static/frontend/selects/containers/PieSelect.jsx
                // when placeholder here is null
                {...(placeholder ? { placeholder: placeholder } : {})}
            />
        </div>
    )
}

export const OfficialSelectNoEU = (props) => <OfficialSelect SelectComponent={OfficialPieSelect} {...props} />
export const OfficialSelect = <TFormValues,>({
    SelectComponent,
    disabled,
    extraFields,
    filterKey,
    label,
    multi,
    name,
    onChange,
    onClear,
    permanentFilter,
    placeholder,
    rules,
    singleSelectSearchify,
    tooltip,
}: PieSelectProps<TFormValues> & {
    extraFields?: any
    filterKey?: any
    onChange?: any
    onClear?: any
}) => (
    <GenericSelect
        SelectComponent={SelectComponent}
        disabled={disabled}
        extraFields={extraFields}
        filterKey={filterKey}
        label={label}
        labelPieSelect="name"
        model={DjangIO.app.person.models.NewPerson}
        multi={multi}
        name={name}
        onChange={onChange}
        onClear={onClear}
        permanentFilter={permanentFilter}
        placeholder={placeholder}
        rules={rules}
        singleSelectSearchify={singleSelectSearchify}
        tooltip={tooltip}
    />
)

export const StaffSelect = <TFormValues,>({
    disabled,
    filterKey,
    label,
    name,
    placeholder,
    rules,
    tooltip,
}: PieSelectProps<TFormValues> & { filterKey?: any }) => (
    <GenericSelect
        disabled={disabled}
        filterKey={filterKey}
        label={label}
        labelPieSelect="name"
        model={DjangIO.app.person.models.NewPerson}
        name={name}
        placeholder={placeholder}
        rules={rules}
        SelectComponent={StafferSelect}
        tooltip={tooltip}
    />
)

export const PressSelect = <TFormValues,>({
    name,
    label,
    tooltip,
    rules,
    placeholder,
    disabled,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        disabled={disabled}
        label={label}
        labelPieSelect="name"
        model={DjangIO.app.person.models.NewPerson}
        name={name}
        placeholder={placeholder}
        rules={rules}
        SelectComponent={PressContactSelect}
        tooltip={tooltip}
    />
)

export const TeamMemberSelect = <TFormValues,>({
    name,
    className,
    label,
    tooltip,
    rules,
    placeholder,
    disabled,
    onChange,
    shouldUnregister = true,
    searchify = true,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        disabled={disabled}
        className={className}
        label={label}
        labelPieSelect="name"
        model={DjangIO.app.userdata.models.QuorumUser}
        name={name}
        placeholder={placeholder}
        rules={rules}
        onChange={onChange}
        SelectComponent={UserSelect}
        shouldUnregister={shouldUnregister}
        searchify={searchify}
        tooltip={tooltip}
    />
)

export const SupporterSelect = <TFormValues,>({
    name,
    label,
    tooltip,
    rules,
    placeholder,
    disabled,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        disabled={disabled}
        label={label}
        labelPieSelect="name"
        model={DjangIO.app.grassroots.models.Supporter}
        name={name}
        placeholder={placeholder}
        rules={rules}
        SelectComponent={ContactSelect}
        tooltip={tooltip}
    />
)

export const PacSupporterSelect = <TFormValues,>({
    name,
    label,
    tooltip,
    rules,
    placeholder,
    disabled,
    multi,
    clearable,
    permanentFilter,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        withAsterisk
        disabled={disabled}
        label={label}
        labelPieSelect="name"
        model={DjangIO.app.grassroots.models.Supporter}
        name={name}
        placeholder={placeholder}
        rules={rules}
        SelectComponent={PacContactSelect}
        tooltip={tooltip}
        multi={multi}
        clearable={clearable}
        permanentFilter={{ for_pac_eligibilities: Userdata.ledger_settings_ids, ...permanentFilter }}
    />
)

export const PublicOrgSelect = <TFormValues,>({
    clearable,
    disabled,
    label,
    multi,
    name,
    permanentFilter,
    placeholder,
    rules,
    singleSelectSearchify,
    tooltip,
    withAsterisk,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        disabled={disabled}
        label={label}
        labelPieSelect="name"
        model={DjangIO.app.person.models.PublicOrganization}
        multi={multi}
        name={name}
        permanentFilter={permanentFilter}
        placeholder={placeholder}
        rules={rules}
        SelectComponent={PublicOrganizationSelect}
        singleSelectSearchify={singleSelectSearchify}
        tooltip={tooltip}
        withAsterisk={withAsterisk}
        clearable={clearable}
    />
)

export const BillTermsSelect = <TFormValues,>({
    name,
    label,
    tooltip,
    rules,
    placeholder,
    disabled,
    shouldUnregister,
    multi,
    clearable,
    className,
    ...props
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        className={className}
        disabled={disabled}
        label={label}
        labelPieSelect="name"
        model={DjangIO.app.bill.models.BillTerm}
        name={name}
        placeholder={placeholder}
        rules={rules}
        SelectComponent={BillTermSelect}
        tooltip={tooltip}
        shouldUnregister={shouldUnregister}
        multi={multi}
        clearable={clearable}
        {...props}
    />
)

export const BillsSelect = <TFormValues,>({
    name,
    label,
    tooltip,
    rules,
    placeholder,
    disabled,
    shouldUnregister,
    className,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        className={className}
        disabled={disabled}
        label={label}
        labelPieSelect="name"
        model={DjangIO.app.bill.models.Bill}
        name={name}
        placeholder={placeholder}
        rules={rules}
        SelectComponent={BillSelect}
        tooltip={tooltip}
        shouldUnregister={shouldUnregister}
    />
)

export const TrackingDashboardSelect = <TFormValues,>({
    name,
    label,
    tooltip,
    rules,
    placeholder,
    disabled,
    shouldUnregister,
    multi,
    clearable,
    className,
    permanentFilter,
    ...props
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        className={className}
        disabled={disabled}
        label={label}
        labelPieSelect="name"
        model={DjangIO.app.tracking_dashboards.models.TrackingDashboard}
        name={name}
        placeholder={placeholder}
        rules={rules}
        SelectComponent={TrackingDashboardPieSelect}
        tooltip={tooltip}
        shouldUnregister={shouldUnregister}
        multi={multi}
        clearable={clearable}
        permanentFilter={permanentFilter}
        {...props}
    />
)

export const IssueSelect = <TFormValues,>({
    className,
    disabled,
    label,
    multi,
    name,
    permanentFilter,
    placeholder,
    rules,
    searchify,
    shouldUnregister,
    singleSelectSearchify,
    tooltip,
    withAsterisk,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        className={className}
        disabled={disabled}
        label={label}
        labelPieSelect="name"
        model={DjangIO.app.userdata.models.IssueManagement}
        multi={multi}
        name={name}
        permanentFilter={permanentFilter}
        placeholder={placeholder}
        rules={rules}
        searchify={searchify}
        SelectComponent={IssueManagementSelect}
        shouldUnregister={shouldUnregister}
        singleSelectSearchify={singleSelectSearchify}
        tooltip={tooltip}
        withAsterisk={withAsterisk}
    />
)

export const TeamSelect = <TFormValues,>({
    name,
    label,
    tooltip,
    rules,
    placeholder,
    shouldUnregister,
    disabled,
    className,
    onChange,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        disabled={disabled}
        label={label}
        labelPieSelect="name"
        model={DjangIO.app.userdata.models.Team}
        name={name}
        permanentFilter={{
            default_for_user: false,
            organization: Userdata.organization_id,
        }}
        placeholder={placeholder}
        rules={rules}
        SelectComponent={TeamPieSelect}
        tooltip={tooltip}
        shouldUnregister={shouldUnregister}
        className={className}
        onChange={onChange}
    />
)

export const TeamSelectForUser = <TFormValues,>({
    name,
    label,
    tooltip,
    rules,
    placeholder,
    shouldUnregister,
    disabled,
    className,
    searchify,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        disabled={disabled}
        label={label}
        labelPieSelect="name"
        model={DjangIO.app.userdata.models.Team}
        name={name}
        permanentFilter={{
            default_for_user: true,
            organization: Userdata.organization_id,
        }}
        placeholder={placeholder}
        rules={rules}
        SelectComponent={TeamPieSelect}
        tooltip={tooltip}
        shouldUnregister={shouldUnregister}
        className={className}
        searchify={searchify}
    />
)

export const LedgerSettingsSelect = <TFormValues,>({
    name,
    label,
    tooltip,
    rules,
    disabled,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        disabled={disabled}
        SelectComponent={LedgerSettingsPieSelect}
        name={name}
        label={label}
        labelPieSelect="name"
        tooltip={tooltip}
        model={DjangIO.app.ledger.models.LedgerSettings}
        rules={rules}
    />
)

export const ActionCenterSelect = <TFormValues,>({
    name,
    label,
    tooltip,
    rules,
    shouldUnregister,
    disabled,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        disabled={disabled}
        SelectComponent={GrassrootsActionCenterSettingsSelect}
        name={name}
        label={label}
        tooltip={tooltip}
        model={DjangIO.app.grassroots.models.GrassrootsActionCenterSettings}
        rules={rules}
        shouldUnregister={shouldUnregister}
    />
)

export const RegulationsSelect = <TFormValues,>({
    name,
    label,
    tooltip,
    rules,
    placeholder,
    shouldUnregister,
    className,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        className={className}
        SelectComponent={RegulationSelect}
        name={name}
        label={label}
        tooltip={tooltip}
        model={DjangIO.app.reg.models.Regulation}
        rules={rules}
        placeholder={placeholder}
        shouldUnregister={shouldUnregister}
    />
)

export const EmailTemplateSelect = <TFormValues,>({
    name,
    label,
    tooltip,
    rules,
    placeholder,
    multi,
    shouldUnregister,
    withAsterisk,
    className,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        SelectComponent={EmailTemplatePisSelect}
        name={name}
        label={label}
        tooltip={tooltip}
        model={DjangIO.app.userdata.outbox.models.email_template.EmailTemplate}
        rules={rules}
        placeholder={placeholder}
        multi={multi}
        shouldUnregister={shouldUnregister}
        withAsterisk={withAsterisk}
        className={className}
        permanentFilter={{
            exclude_empty_html_content: true,
        }}
    />
)

export const RegistrationForm = <TFormValues,>({
    disabled,
    name,
    label,
    tooltip,
    rules,
    placeholder,
    multi,
    shouldUnregister,
    withAsterisk,
    clearable,
    className,
    permanentFilter,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        SelectComponent={GrassrootsRegistrationPageSelect}
        disabled={disabled}
        name={name}
        label={label}
        tooltip={tooltip}
        model={DjangIO.app.grassroots.models.GrassrootsRegistrationPage}
        rules={rules}
        placeholder={placeholder}
        multi={multi}
        shouldUnregister={shouldUnregister}
        withAsterisk={withAsterisk}
        clearable={clearable}
        className={className}
        permanentFilter={permanentFilter}
    />
)

export const DistrictsSelect = <TFormValues,>({
    disabled,
    label,
    multi,
    name,
    placeholder,
    rules,
    singleSelectSearchify,
    tooltip,
    permanentFilter,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        SelectComponent={DistrictSelect}
        disabled={disabled}
        label={label}
        model={DjangIO.app.geography.models.District}
        multi={multi}
        name={name}
        placeholder={placeholder}
        rules={rules}
        singleSelectSearchify={singleSelectSearchify}
        tooltip={tooltip}
        permanentFilter={permanentFilter}
    />
)

export const PoliticalCommitteesSelect = <TFormValues,>({
    disabled,
    label,
    multi,
    name,
    placeholder,
    rules,
    singleSelectSearchify,
    tooltip,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        SelectComponent={PoliticalCommitteeSelect}
        disabled={disabled}
        label={label}
        model={DjangIO.app.pac.models.political_committee.PoliticalCommittee}
        multi={multi}
        name={name}
        placeholder={placeholder}
        rules={rules}
        singleSelectSearchify={singleSelectSearchify}
        tooltip={tooltip}
    />
)

export const CountiesSelect = <TFormValues,>({
    disabled,
    label,
    multi,
    name,
    placeholder,
    rules,
    singleSelectSearchify,
    tooltip,
    permanentFilter,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        SelectComponent={CountySelect}
        disabled={disabled}
        label={label}
        // @ts-expect-error
        model={DjangIO.app.geography.models.Counties}
        multi={multi}
        name={name}
        placeholder={placeholder}
        rules={rules}
        singleSelectSearchify={singleSelectSearchify}
        tooltip={tooltip}
        permanentFilter={permanentFilter}
    />
)

export const StatesSelect = <TFormValues,>({
    disabled,
    label,
    multi,
    name,
    placeholder,
    rules,
    singleSelectSearchify,
    tooltip,
    onChange,
}: PieSelectProps<TFormValues> & { onChange: any }) => (
    <GenericSelect
        SelectComponent={StateSelect}
        disabled={disabled}
        label={label}
        // @ts-expect-error
        model={DjangIO.app.geography.models.States}
        multi={multi}
        name={name}
        placeholder={placeholder}
        rules={rules}
        singleSelectSearchify={singleSelectSearchify}
        tooltip={tooltip}
        onChange={onChange}
    />
)

export const ElectedOfficesSelect = <TFormValues,>({
    disabled,
    label,
    multi,
    name,
    placeholder,
    rules,
    singleSelectSearchify,
    tooltip,
    permanentFilter,
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        SelectComponent={ElectedOfficeSelect}
        disabled={disabled}
        label={label}
        model={DjangIO.app.pac.models.elected_office.ElectedOffice}
        multi={multi}
        name={name}
        placeholder={placeholder}
        rules={rules}
        singleSelectSearchify={singleSelectSearchify}
        tooltip={tooltip}
        permanentFilter={permanentFilter}
    />
)

export const CustomFieldsSelect = <TFormValues,>({
    disabled,
    label,
    multi = true,
    name,
    placeholder,
    rules,
    singleSelectSearchify,
    tooltip,
    permanentFilter,
    withAsterisk,
    ...props
}: PieSelectProps<TFormValues>) => (
    <GenericSelect
        SelectComponent={CustomTagSelect}
        disabled={disabled}
        label={label}
        labelPieSelect="name"
        model={DjangIO.app.grassroots.models.CustomTag}
        multi={multi}
        name={name}
        placeholder={placeholder}
        rules={rules}
        singleSelectSearchify={singleSelectSearchify}
        tooltip={tooltip}
        permanentFilter={permanentFilter}
        withAsterisk={withAsterisk}
        {...props}
    />
)
